import type {
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyElementJson,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import { SurveyElementFormHandlerDate } from './survey-element-form-handler-date'
import { SurveyElementFormHandlerFile } from './survey-element-form-handler-file'
import { SurveyElementFormHandlerImagepicker } from './survey-element-form-handler-imagepicker'
import { SurveyElementFormHandlerNumber } from './survey-element-form-handler-number'
import { SurveyElementFormHandlerPanel } from './survey-element-form-handler-panel'
import { SurveyElementFormHandlerRating } from './survey-element-form-handler-rating'
import { SurveyElementFormHandlerSurveyModel } from './survey-element-form-handler-survey-model'
import { SurveyElementFormHandlerText } from './survey-element-form-handler-text'

export interface SurveyElementFormHandler {
  elementJson: SurveyElementJson
  surveyModel: SurveyModel | null
  data: SurveyAnswerRecord
  getValue: () => SurveyAnswerValue
  getComment: () => string
  setElementResultRecordValue: (value: SurveyAnswerValue) => void
  setElementResultRecordComment: (comment: string) => void
  validate: () => {
    isValid: boolean
    errorMessage?: string
  }
  setUploadingState?: (isUploading: boolean) => void
  getUploadingState?: () => boolean
}

/**
 * SurveyModelをproxyするためのクラス
 */
export class SurveyElementForm implements SurveyElementFormHandler {
  #handler: SurveyElementFormHandlerSurveyModel
    | SurveyElementFormHandlerImagepicker
    | SurveyElementFormHandlerFile
    | SurveyElementFormHandlerText
    | SurveyElementFormHandlerNumber
    | SurveyElementFormHandlerDate
    | SurveyElementFormHandlerPanel
    | SurveyElementFormHandlerRating

  constructor(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
    surveyModel: SurveyModel | null,
  ) {
    this.#handler = this._buildHandler(elementJson, elementResultRecord, surveyModel)
  }

  _buildHandler(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
    surveyModel: SurveyModel | null,
  ) {
    switch (elementJson.type) {
      case 'file':
        return new SurveyElementFormHandlerFile(
          elementJson,
          elementResultRecord,
        )
      case 'imagepicker':
        return new SurveyElementFormHandlerImagepicker(
          elementJson,
          elementResultRecord,
        )
      case 'text':
        return new SurveyElementFormHandlerText(
          elementJson,
          elementResultRecord,
        )
      case 'comment':
        return new SurveyElementFormHandlerText(
          elementJson,
          elementResultRecord,
        )
      case 'number':
        return new SurveyElementFormHandlerNumber(
          elementJson,
          elementResultRecord,
        )
      case 'date':
        return new SurveyElementFormHandlerDate(
          elementJson,
          elementResultRecord,
        )
      case 'panel':
        return new SurveyElementFormHandlerPanel(
          elementJson,
          elementResultRecord,
        )
      case 'rating':
        return new SurveyElementFormHandlerRating(
          elementJson,
          elementResultRecord || undefined,
          surveyModel || undefined,
        )
      default:
        return new SurveyElementFormHandlerSurveyModel(
          elementJson,
          surveyModel,
        )
    }
  }

  get elementJson() {
    return this.#handler.elementJson
  }

  get surveyModel() {
    return this.#handler.surveyModel
  }

  /**
   * custom elementの場合は、setElementResultRecordによってelementResultRecordにデータがセットされるので
   * elementResultRecordから取得
   * custom elementではない場合には、surveyModel.dataから取得
   * NOTE: SurveyElementFileは、custom elementだけど、内部でSurveyModelを使っているので surveyModel.dataから取得
   */
  get data(): SurveyAnswerRecord {
    return this.#handler.data
  }

  /**
   * 現在のelementResultRecordから、値の部分だけを取得する
   */
  getValue() {
    return this.#handler.getValue()
  }

  /**
   * castしている。
   * SurveyResultRecord schema的にnumberやbooleanもあるため、しかし、Commentはstringのみなのでcastしている。
   * TODO: castせず型推論で対応したい
   */
  getComment(): string {
    return this.#handler.getComment()
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    this.#handler.setElementResultRecordValue(value)
  }

  setElementResultRecordComment(comment: string) {
    this.#handler.setElementResultRecordComment(comment)
  }

  /**
   * formSchemaがある場合は、zodでvalidateする (@see createSurveyElementSchema)
   * formSchemaがない場合は、surveyModelでvalidateする
   */
  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    return this.#handler.validate()
  }

  setUploadingState(isUploading: boolean): void {
    if ('setUploadingState' in this.#handler) {
      this.#handler.setUploadingState(isUploading)
    }
  }

  getUploadingState(): boolean {
    return 'getUploadingState' in this.#handler ? this.#handler.getUploadingState() : false
  }
}
